import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { RecurlyProvider } from '@recurly/react-recurly';
import { ThemeProvider, ThemeContext } from 'styled-components';
import nebulaTheme from '@nebula/theme';
import dayjs from 'dayjs';
import { ErrorAlert, InformationAlert, WarningAlert } from '@nebula/alert';
import { Snackbar } from '@nebula/snackbar';
import GlobalStyles from '@nebula/global-styles';
import PropTypes from 'prop-types';
import Spinner from '@nebula/spinner';
import { useMediaQuery } from '@nebula/hooks';
import { useDrawer } from '@nebula/drawer';
import {
  isFlutterApp,
  navigateTo,
  MobileLocations,
  ChangeBillingDateStage,
  changeBillingDateTransition,
} from '@ecs-uk/web-common-mobile-view-helpers';
import { ChevronIcon } from '@nebula/icons/ui';
import ChangePaymentMethodDrawer from './change-payment-method/index.tsx';
import getChangeBillingDateEligibility from '../../actions/billing/getChangeBillingDateEligibility';
import { isAnnually } from '../../helpers/flex-helpers/flexBillingFrequency';
import {
  CurrentPaymentDetails,
  PaymentIconContainer,
  PaymentIcon,
  HorizontalLine,
  ExpiryText,
  BillingDetailsSection,
  BillingDateContainer,
  BillingDateLeft,
  HeadingLabel,
  HeadingValue,
  PaymentandBillingContainer,
  PaymentText,
  AlertContainer,
  Subheading,
  Heading,
  PaymentTextContainer,
  Container,
  InformationAlertContainer,
  ChangeDateButton,
  FreeTrialDescription,
  ErrorAlertContainer,
  BillingDetailsHeader,
  BillingDetailsLoaderContainer,
  CenteredSnackbarContainer,
  PaymentDetailsContainer,
  ChangePaymentMethodContainer,
  ChangePaymentMethodButton,
  SpinnerContainer,
  BackLinkWrapper,
} from './index.styles';
import { Alert } from '../../components/PaymentMethodChange/index.styles';
import PaymentContextProvider, {
  PAYMENT_STAGES,
  PaymentContext,
} from '../../components/PaymentOrchestrator/PaymentContextProvider';
import { getEnv } from '../../components/Context/env';
import InViewComponent from '../../components/InView';
import { PaymentMethodTypes } from '../../components/PaymentCaptureMethod/constants.ts';
import daysLeftOfTrialInCurrentProductSubscription from '../../actions/ecd/resolvers/daysLeftOfTrialInCurrentProductSubscription';
import { fulfillmentStatuses, inEligibleReasons, queryParams, splitsList, stateVariables } from '../../constants';
import useFlexData from '../../hooks/flexHooks/useFlexData';
import { getFamilyBillingPeriods } from '../../helpers/flex-helpers/findFlexPlanByFamily';
import {
  publishChangeBillDateClick,
  publishChangeBillDateEligibilityData,
  trackChangeBillingDateAvailability,
} from '../../helpers/changeBillingDate/analytics';
import usePageView from '../../hooks/usePageView';
import useExistingPaymentMethodDisplay from '../../components/PaymentCaptureMethod/hooks/useExistingPaymentMethodDisplay';
import useSplits from '../../hooks/useSplits';
import usePaymentMethodSupported from '../../hooks/usePaymentMethodSupported';
import { FAMILIES_NOT_REQUIRING_PAYMENT } from '../../constants/payment';
import { flexProductFamilies } from '../../constants/products.ts';
import { switchInfo } from '../../helpers';
import flexSwitchInfo from '../../helpers/flex-helpers/switchInfo';
import { trackPaymentMethodChangeStarted } from '../../helpers/switch/analytics';
import usePaymentMethods from '../../components/PaymentOrchestrator/hooks/usePaymentMethods';
import { trackChangePaymentMethodAvailability } from '../../helpers/changePaymentMethodNewJourney/analytics.ts';

const PAGE_NAME = 'ProdMovePaymentAndBillingPage';

const PaymentBillingContent = ({ isChangeBillingPeriodEnabled, entitlements, isFlexFlow }) => {
  const theme = useContext(ThemeContext);
  const { existingPaymentType, paymentIcon, paymentMethodDisplayText, cardExpiryDateShort, paymentAriaLabel } =
    useExistingPaymentMethodDisplay();
  const { paymentStage, paymentChangeActive, isWebview, goToPaymentStage } = useContext(PaymentContext);
  const history = useHistory();
  const isLoading = useSelector(state => state.billing?.dateChangeEligibility.loading);
  const eligibility = useSelector(state => state.billing?.dateChangeEligibility.data);
  const location = useLocation();
  const { REACT_APP_HOME_URL: homepageUrl } = getEnv();
  // TODO: Temporary route-state hook for snackbar testing; remove once wired to backend response/state.
  const billingPeriodStatus = location.state?.billingPeriodStatus;
  const [showSnackbar, setShowSnackbar] = useState(!!billingPeriodStatus);

  useEffect(() => {
    if (billingPeriodStatus) {
      setShowSnackbar(true);
    }
  }, [billingPeriodStatus]);

  const isInRecycle = useSelector(state => state?.billing?.billingStateRecycle);
  const reduxState = useSelector(state => state);
  const daysLeftInTrial = isFlexFlow
    ? entitlements.daysLeftForTrialToOver ?? 0
    : daysLeftOfTrialInCurrentProductSubscription(reduxState);
  const isInTrial = !eligibility.isEligible && eligibility.ineligibleReason === inEligibleReasons.IsInTrialPeriod;
  const { isShowing, toggle } = useDrawer();

  const splits = useSplits([splitsList.billingDeclineIncidentJuly25, splitsList.billingIncidentModalPaymentChange]);
  const billingDeclineIncidentJuly25 = splits[splitsList.billingDeclineIncidentJuly25];
  const billingIncidentModalPaymentChange = splits[splitsList.billingIncidentModalPaymentChange];
  const showBillingDeclineIncidentAlert = billingDeclineIncidentJuly25 && !billingIncidentModalPaymentChange;
  const isDesktop = useMediaQuery(theme.media.tabletPortraitMin);
  const changePaymentMethodButtonRef = useRef(null);
  const availablePaymentMethods = usePaymentMethods();
  const reduxNextBillingDate = useSelector(state => state.billing?.nextBillingDate);
  const nextBillingDate = isFlexFlow
    ? entitlements?.plan?.nextBillDate ?? entitlements?.plans?.[0]?.nextBillDate
    : reduxNextBillingDate;

  const paymentDetailsAriaLabel = `Your chosen payment method is ${paymentAriaLabel}${
    existingPaymentType === PaymentMethodTypes.Card ? `. Expires ${cardExpiryDateShort}` : ''
  }`;

  const openChangePaymentMethodDrawer = () => {
    trackPaymentMethodChangeStarted(availablePaymentMethods, existingPaymentType);
    goToPaymentStage(PAYMENT_STAGES.REQUIRES_PAYMENT_METHOD);
    if (isFlutterApp()) {
      navigateTo(MobileLocations.CHANGE_PAYMENT_METHOD_MODAL);
    } else {
      toggle();
    }
  };

  const handleBackClick = () => {
    const fromHome = location.state?.fromHome;
    if (fromHome) {
      window.location.href = homepageUrl;
    } else if (window.history.length <= 2) {
      // When users access the /billing page directly, history length may be 2 or less (depending on how they got there),
      // so we should navigate them to the subscription page instead of going back to an empty page.
      history.push('/');
    } else {
      history.goBack();
    }
  };

  const handleReturnFocus = () => {
    // Return focus to the change payment method button when drawer closes
    if (changePaymentMethodButtonRef.current) {
      changePaymentMethodButtonRef.current.focus();
      return true;
    }
    return false;
  };

  usePageView(PAGE_NAME);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (eligibility.isEligible !== null) {
        publishChangeBillDateEligibilityData({
          isEligible: eligibility.isEligible,
          ineligibilityReason: eligibility.ineligibleReason,
        });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [eligibility.isEligible, eligibility.ineligibleReason]);

  useEffect(() => {
    if (isWebview) {
      changeBillingDateTransition({ stage: ChangeBillingDateStage.INITIAL });
    } else {
      // Delay focusing slightly to ensure the [data-back-link="true"] element
      // has been rendered and attached to the DOM before querying and focusing it.
      const timer = setTimeout(() => {
        const backLinkElement = document.querySelector('[data-back-link="true"]');
        if (backLinkElement) {
          backLinkElement.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isWebview]);

  return (
    <PaymentandBillingContainer>
      <PaymentDetailsContainer>
        {!isWebview && (
          <BackLinkWrapper id="back-link" data-back-link="true" tabIndex={0} onClick={handleBackClick}>
            Back
          </BackLinkWrapper>
        )}
        {isChangeBillingPeriodEnabled && (
          <CenteredSnackbarContainer>
            <Snackbar
              isShowing={showSnackbar && billingPeriodStatus === 'success'}
              message="Billing period updated successfully"
              type="success"
              duration={3000}
              onClose={() => setShowSnackbar(false)}
            />
          </CenteredSnackbarContainer>
        )}
        {isChangeBillingPeriodEnabled && (
          <CenteredSnackbarContainer>
            <Snackbar
              isShowing={showSnackbar && billingPeriodStatus === 'error'}
              message="Failed to update billing period"
              type="error"
              duration={3000}
              onClose={() => setShowSnackbar(false)}
            />
          </CenteredSnackbarContainer>
        )}
        <AlertContainer>
          {showBillingDeclineIncidentAlert && paymentStage !== PAYMENT_STAGES.PAYMENT_METHOD_UPDATED && (
            <Alert>
              <ErrorAlertContainer>
                <ErrorAlert
                  title={
                    existingPaymentType === PaymentMethodTypes.Card
                      ? 'Please re-add your payment method'
                      : 'Payment issue detected'
                  }
                  message={
                    existingPaymentType === PaymentMethodTypes.Card
                      ? 'We detected an issue with your details during your last payment.'
                      : 'Please add your payment details again or choose a different method.'
                  }
                  showIcon
                />
              </ErrorAlertContainer>
            </Alert>
          )}
          {billingIncidentModalPaymentChange &&
            existingPaymentType === PaymentMethodTypes.Card &&
            paymentStage !== PAYMENT_STAGES.PAYMENT_METHOD_UPDATED && (
              <Alert>
                <ErrorAlertContainer>
                  <WarningAlert
                    title="Update your payment details"
                    message="Your payment card is changing from Mastercard to Visa."
                    showIcon
                  />
                </ErrorAlertContainer>
              </Alert>
            )}
          {isInRecycle && (
            <InViewComponent label="recycle_payment_method_alert" multi>
              <Alert>
                <ErrorAlertContainer>
                  <ErrorAlert
                    title="Payment declined"
                    message="Please make sure your payment details are up to date and that you have enough funds available."
                    showIcon
                  />
                </ErrorAlertContainer>
              </Alert>
            </InViewComponent>
          )}
          {!nextBillingDate && (
            <Alert>
              <ErrorAlertContainer>
                <ErrorAlert
                  title="Technical error"
                  message="Unfortunately, we are unable to retrieve all your data. Please try again later."
                  showIcon
                />
              </ErrorAlertContainer>
            </Alert>
          )}
        </AlertContainer>
        {!isWebview && <Heading>Payment and billing</Heading>}
        {existingPaymentType && (
          <React.Fragment>
            <Subheading>Payment details</Subheading>
            {isInTrial && (
              <FreeTrialDescription>
                You’re free trial ends in{' '}
                <b>
                  {daysLeftInTrial} day{daysLeftInTrial !== 1 ? 's' : ''}
                </b>{' '}
                - you won’t be charged until then.
              </FreeTrialDescription>
            )}
            <Container>
              <CurrentPaymentDetails aria-label={paymentDetailsAriaLabel}>
                <PaymentIconContainer aria-hidden="true">
                  <PaymentIcon src={paymentIcon} alt={paymentMethodDisplayText} />
                </PaymentIconContainer>
                <PaymentTextContainer aria-hidden="true">
                  <PaymentText card={existingPaymentType === PaymentMethodTypes.Card}>
                    {paymentMethodDisplayText}
                  </PaymentText>
                  {existingPaymentType === PaymentMethodTypes.Card && (
                    <ExpiryText>Expires {cardExpiryDateShort}</ExpiryText>
                  )}
                </PaymentTextContainer>
              </CurrentPaymentDetails>

              <ChangePaymentMethodContainer>
                <ChangePaymentMethodButton
                  ref={changePaymentMethodButtonRef}
                  type="button"
                  label="Change payment method"
                  buttonSize={isDesktop ? 'large' : 'small'}
                  buttonKind="primaryMagenta"
                  disabled={paymentChangeActive}
                  onClick={openChangePaymentMethodDrawer}
                  data-automation-test-element="change-payment-method"
                />
              </ChangePaymentMethodContainer>
            </Container>
          </React.Fragment>
        )}
      </PaymentDetailsContainer>

      <BillingDetailsSection $noMarginTop={!existingPaymentType}>
        <BillingDetailsHeader>
          <Subheading>Billing details</Subheading>
        </BillingDetailsHeader>

        {isLoading ? (
          <BillingDetailsLoaderContainer>
            <Spinner spinnerSize="32px" />
          </BillingDetailsLoaderContainer>
        ) : (
          <BillingDetailsContent
            isWebview={isWebview}
            nextBillingDate={nextBillingDate}
            isChangeBillingPeriodEnabled={isChangeBillingPeriodEnabled}
            entitlements={entitlements}
            isFlexFlow={isFlexFlow}
          />
        )}
      </BillingDetailsSection>
      <ChangePaymentMethodDrawer isShowing={isShowing} toggleDrawer={toggle} returnFocus={handleReturnFocus} />
    </PaymentandBillingContainer>
  );
};

PaymentBillingContent.propTypes = {
  isChangeBillingPeriodEnabled: PropTypes.bool,
  entitlements: PropTypes.object,
  isFlexFlow: PropTypes.bool,
};

const BillingDetailsContent = ({
  isWebview,
  nextBillingDate,
  isChangeBillingPeriodEnabled,
  entitlements,
  isFlexFlow,
}) => {
  const history = useHistory();
  const billingPrice = useSelector(state => state.billing?.signupPrice);
  const price = isFlexFlow ? entitlements?.planPrice ?? entitlements?.plan?.price : billingPrice;
  const { isEligible, ineligibleReason } = useSelector(state => state.billing?.dateChangeEligibility.data);
  const subscription = useSelector(state => state.subscriptions.current);
  const isInRecycle = useSelector(state => state?.billing?.billingStateRecycle);
  const nextBillDateLong = nextBillingDate ? dayjs(nextBillingDate).format('D MMMM YYYY') : 'Unavailable';

  const isFulfillmentSucceeded = isFlexFlow
    ? entitlements.fulfillmentStatus === fulfillmentStatuses.succeeded
    : subscription.fulfillmentStatus === fulfillmentStatuses.succeeded;

  const billingFrequencyVal = subscription?.terms?.[0]?.billingFrequency;

  const isInactiveSubscription = !isEligible && ineligibleReason === inEligibleReasons.Inactive;

  const isInTrial = !isEligible && ineligibleReason === inEligibleReasons.IsInTrialPeriod;

  const showAlreadyAdjustedAlert = !isEligible && ineligibleReason === inEligibleReasons.AlreadyAdjustedInCurrentPeriod;

  const isAnnualUser = isFlexFlow
    ? isAnnually(entitlements?.termFrequency ?? entitlements?.plan?.termFrequency)
    : isAnnually(billingFrequencyVal);

  const getBillingPeriodDisplay = () => {
    if (isAnnualUser) {
      return 'Annual subscription';
    }
    return 'Monthly subscription';
  };

  const getBillingDateDisplay = () => {
    if (!nextBillingDate) {
      return 'Unavailable';
    }
    if (isAnnualUser) {
      return `${dayjs(nextBillingDate).format('D MMMM')} every year`;
    }
    return `${dayjs(nextBillingDate).format('Do')} of every month`;
  };

  const splits = useSplits([
    splitsList.webNewChangePaymentMethod,
    splitsList.webChangeBillingDate,
    splitsList.flutterCbdToggle,
  ]);
  const isInWebChangeBillingDateSplit = splits[splitsList.webChangeBillingDate];
  const isCBDMobileSplitEnabled = splits[splitsList.flutterCbdToggle];

  const isCBDEnabled = !isWebview
    ? isInWebChangeBillingDateSplit
    : isCBDMobileSplitEnabled && isInWebChangeBillingDateSplit;

  // check if the existing payment method is supported on the platform
  const isPaymentMethodSupported = usePaymentMethodSupported();

  const handleChangeDateClick = () => {
    publishChangeBillDateClick();

    const changeDatePath = `${isWebview ? '/mobile' : ''}/billing/change-date`;
    history.push(changeDatePath);
  };

  const handleChangeBillingPeriodClick = () => {
    const targetPeriod = isAnnualUser ? 'monthly' : 'annual';

    if (isWebview) {
      navigateTo(isAnnualUser ? MobileLocations.CHANGE_TO_MONTHLY_BILLING : MobileLocations.CHANGE_TO_ANNUAL_BILLING);
      return;
    }

    history.push(`/switch?switchingTo=${subscription.family}&${queryParams.billingPeriod}=${targetPeriod}`);
  };

  return (
    <React.Fragment>
      {isInWebChangeBillingDateSplit && (isInRecycle || isInTrial || isEligible || showAlreadyAdjustedAlert) && (
        <InformationAlertContainer>
          {isInRecycle && (
            <InformationAlert
              data-testid="recycle_change_billing_date_alert"
              message="If you need to change your billing date update your payment method first."
              showIcon
            />
          )}
          {isInTrial && (
            <InformationAlert
              data-testid="trial_change_billing_date_alert"
              message="If you need to change your billing date please wait until after your first payment."
              showIcon
            />
          )}
          {isEligible && (
            <InformationAlert
              data-testid="generic_change_billing_date_alert"
              title="Need to change your billing date?"
              message="Currently you can change it once per billing period."
              showIcon
            />
          )}
          {showAlreadyAdjustedAlert && (
            <InformationAlert
              data-testid="already_adjusted_change_billing_date_alert"
              message="Need to change your billing date again? You must wait until after your next bill."
              showIcon
            />
          )}
        </InformationAlertContainer>
      )}

      {isChangeBillingPeriodEnabled && (
        <React.Fragment>
          <BillingDateContainer flexRow>
            <BillingDateLeft>
              <HeadingLabel>Billing period</HeadingLabel>
              <HeadingValue>{getBillingPeriodDisplay()}</HeadingValue>
            </BillingDateLeft>
            <ChangeDateButton
              type="button"
              aria-label="Change billing period"
              tabIndex={0}
              disabled={isInRecycle}
              onClick={handleChangeBillingPeriodClick}
            >
              Change <ChevronIcon direction="right" iconSize="xxSmall" iconColor={nebulaTheme.color.ui.blue[70]} />
            </ChangeDateButton>
          </BillingDateContainer>
          <HorizontalLine aria-hidden="true" />
        </React.Fragment>
      )}

      <BillingDateContainer flexRow>
        <BillingDateLeft>
          <HeadingLabel>Billing date</HeadingLabel>
          <HeadingValue>{getBillingDateDisplay()}</HeadingValue>
        </BillingDateLeft>
        {isCBDEnabled &&
          isPaymentMethodSupported &&
          !isAnnualUser &&
          !isInactiveSubscription &&
          isFulfillmentSucceeded &&
          (isInRecycle || isInTrial || isEligible || showAlreadyAdjustedAlert) && (
            <ChangeDateButton
              id="change_billing_date"
              disabled={showAlreadyAdjustedAlert || isInRecycle || isInTrial}
              onClick={handleChangeDateClick}
              aria-label="Change billing date"
              tabIndex={0}
            >
              Change <ChevronIcon direction="right" iconSize="xxSmall" iconColor={nebulaTheme.color.ui.blue[70]} />
            </ChangeDateButton>
          )}
      </BillingDateContainer>
      <HorizontalLine aria-hidden="true" />
      <BillingDateContainer>
        <HeadingLabel>Next bill</HeadingLabel>
        <HeadingValue>{nextBillDateLong}</HeadingValue>
      </BillingDateContainer>
      <HorizontalLine aria-hidden="true" />
      <BillingDateContainer>
        <HeadingLabel>Price:</HeadingLabel>
        <HeadingValue>£{nextBillingDate ? price : '0'}</HeadingValue>
      </BillingDateContainer>
    </React.Fragment>
  );
};

BillingDetailsContent.propTypes = {
  isWebview: PropTypes.bool,
  nextBillingDate: PropTypes.string,
  isChangeBillingPeriodEnabled: PropTypes.bool,
  entitlements: PropTypes.object,
  isFlexFlow: PropTypes.bool,
};

const isCriticalError = errors => {
  if (!errors?.length) return false;
  return errors.some(error => error.type === 'Error' && error.critical);
};

const PaymentandBilling = ({ isWebview }) => {
  const { REACT_APP_RECURLY_PUBLIC_KEY: key } = getEnv();
  const { entitlements, products: allProducts } = useFlexData();
  const isFlexFlow = Boolean(entitlements);

  const currentOffer = useSelector(state => state.offers?.currentOffer);
  const subscriptionFreeTrial = useSelector(state => state.subscriptions?.current?.freeTrial);
  const entitlementPlan = entitlements?.plan ?? entitlements?.plans?.[0];
  const isTrial = isFlexFlow
    ? Boolean(
        (entitlements?.plan?.freeTrialEndDt ?? entitlementPlan?.freeTrialEndDt) &&
          dayjs(entitlements?.plan?.freeTrialEndDt ?? entitlementPlan?.freeTrialEndDt).isAfter(dayjs())
      )
    : subscriptionFreeTrial;
  const billing = useSelector(state => state.billing);
  const splitReady = useSelector(state => state.split?.fetched);
  const currentSubscription = useSelector(state => state.subscriptions?.current);
  const currentSubscriptionFamily = isFlexFlow ? entitlements?.family : currentSubscription?.family;
  const isFreeOrBreachUser = FAMILIES_NOT_REQUIRING_PAYMENT.includes(currentSubscriptionFamily);

  const billingFrequencyVal = currentSubscription?.terms?.[0]?.billingFrequency;
  const isAnnualUser = isFlexFlow
    ? isAnnually(entitlements?.termFrequency ?? entitlements?.plan?.termFrequency)
    : isAnnually(billingFrequencyVal);
  const isFulfillmentSucceeded = isFlexFlow
    ? entitlements.fulfillmentStatus === fulfillmentStatuses.succeeded
    : currentSubscription?.fulfillmentStatus === fulfillmentStatuses.succeeded;
  const isInRecycle = useSelector(state => state?.billing?.billingStateRecycle);
  const errors = useSelector(state => state.error);

  const history = useHistory();
  const dispatch = useDispatch();

  let cardSavePermitted;
  if (isFlexFlow) {
    cardSavePermitted = flexSwitchInfo(entitlements).switchPermitted;
  } else if (billing && currentSubscription) {
    cardSavePermitted = switchInfo(billing, currentSubscription).cardSavePermitted;
  } else {
    cardSavePermitted = false;
  }

  const splits = useSplits([
    splitsList.webNewChangePaymentMethod,
    splitsList.webChangeBillingDate,
    splitsList.flutterCbdToggle,
    splitsList.annualPricing,
    splitsList.prodmoveNewDesign,
  ]);
  const webNewChangePaymentMethodSplit = splits[splitsList.webNewChangePaymentMethod];
  const webChangeBillingDate = splits[splitsList.webChangeBillingDate];
  const isCBDMobileSplitEnabled = splits[splitsList.flutterCbdToggle];
  const isCBDEnabled = !isWebview ? webChangeBillingDate : isCBDMobileSplitEnabled && webChangeBillingDate;
  const { hasBothBillingPeriods } = getFamilyBillingPeriods(currentSubscriptionFamily, allProducts?.eligiblePlans);
  const canChangeSubscription = cardSavePermitted && isFulfillmentSucceeded;
  const canChangeBillingPeriod =
    canChangeSubscription && hasBothBillingPeriods && Boolean(splits[splitsList.annualPricing]);
  const isChangeBillingPeriodEnabled =
    isFlexFlow && entitlementPlan?.family === flexProductFamilies.identity && canChangeBillingPeriod;

  // Check if required data is still loading
  const isDataLoading = !errors?.length && !currentSubscriptionFamily;

  const canLoadPaymentAndBilling =
    splitReady &&
    currentSubscriptionFamily &&
    !isFreeOrBreachUser &&
    cardSavePermitted &&
    (webNewChangePaymentMethodSplit || webChangeBillingDate);

  const isPaymentMethodSupported = usePaymentMethodSupported();

  useEffect(() => {
    if (!isCriticalError(errors) && !splitReady) return;

    if (!isDataLoading && !canLoadPaymentAndBilling) {
      if (isWebview) {
        window?.navigateToMySubscription?.postMessage('');
      } else {
        history.push('/', { [stateVariables.persistErrorsOnLocationChange]: true });
      }
      return;
    }

    trackChangeBillingDateAvailability(isCBDEnabled ? 'B' : 'A');
    trackChangePaymentMethodAvailability(webNewChangePaymentMethodSplit);

    if (
      canLoadPaymentAndBilling &&
      isCBDEnabled &&
      isPaymentMethodSupported &&
      !isAnnualUser &&
      isFulfillmentSucceeded &&
      !isInRecycle
    ) {
      dispatch(getChangeBillingDateEligibility());
    }
  }, [
    isDataLoading,
    canLoadPaymentAndBilling,
    history,
    isCBDEnabled,
    dispatch,
    isWebview,
    webNewChangePaymentMethodSplit,
    splitReady,
    isPaymentMethodSupported,
    isAnnualUser,
    isFulfillmentSucceeded,
    isInRecycle,
  ]);

  if (isDataLoading || !canLoadPaymentAndBilling) {
    return (
      <ThemeProvider theme={nebulaTheme}>
        <GlobalStyles />
        <SpinnerContainer data-automation-test-element="spinner-container">
          <Spinner spinnerSize="2rem" />
        </SpinnerContainer>
      </ThemeProvider>
    );
  }

  const currentPlan =
    isFlexFlow && entitlementPlan
      ? {
          family: currentSubscriptionFamily ?? entitlementPlan.family,
          product: {
            planCode: entitlementPlan.planCode,
            name: entitlementPlan.name,
            price: entitlements?.planPrice ?? entitlementPlan.price,
            term: entitlements?.termFrequency ?? entitlementPlan.termFrequency ?? 'MONTHLY',
            trialLength: isTrial ? entitlementPlan.trialLength ?? 30 : 0,
          },
        }
      : null;

  const currentFlexSubscription =
    isFlexFlow && entitlementPlan
      ? {
          family: currentSubscriptionFamily ?? entitlementPlan.family,
          planCode: entitlementPlan.planCode,
          freeTrial: Boolean(isTrial),
          termFrequency: entitlements?.termFrequency ?? entitlementPlan.termFrequency,
          price: entitlements?.planPrice ?? entitlementPlan.price,
        }
      : null;

  return (
    <PaymentContextProvider
      upgradeOffer={isFlexFlow ? null : currentOffer ?? {}}
      upgradePlan={currentPlan}
      currentFlexSubscription={currentFlexSubscription}
      isFlexFlow={isFlexFlow}
      isTrial={Boolean(isTrial)}
      isMethodChange
      isWebview={isWebview}
    >
      <RecurlyProvider publicKey={key}>
        <ThemeProvider theme={nebulaTheme}>
          <GlobalStyles />
          <PaymentBillingContent
            isChangeBillingPeriodEnabled={isChangeBillingPeriodEnabled}
            entitlements={entitlements}
            isFlexFlow={isFlexFlow}
          />
        </ThemeProvider>
      </RecurlyProvider>
    </PaymentContextProvider>
  );
};

PaymentandBilling.propTypes = {
  isWebview: PropTypes.bool,
};

export default PaymentandBilling;
